import type { EccReceiptRequest, EccReceiptResult } from '@/features/payments/ecc/contract';
import { api } from './client';
import { i18nMap } from '@/lib/i18n-map';

export type PayMethod =
  | 'cash'
  | 'card'
  | 'transfer'
  | 'other'
  | 'mixed'
  | 'online'
  /** Round 2B: a gift certificate spent (accounting add-on only). */
  | 'certificate';

/** Metodele pe care recepția le poate alege ea — `mixed` vine din împărțire. */
export type SinglePayMethod = Exclude<PayMethod, 'mixed'>;

/** O parte dintr-o încasare plătită din două: „300 numerar". */
export interface PaymentSplitPart {
  method: SinglePayMethod;
  amount: number;
  /** Casa părții în numerar; fără ea, casa încasării (contabilitate §2.6). */
  cashDeskId?: string | null;
  /** Contul în care intră partea pe card, transfer sau online. */
  bankAccountId?: string | null;
}

export interface Payment {
  id: string;
  patientId: string | null;
  patientName: string;
  appointmentId: string | null;
  doctorId: string | null;
  service: string | null;
  /** Cine a plătit, când nu pacientul (etapa 6). */
  payerName: string | null;
  payerPersonId: string | null;
  amount: number;
  payMethod: PayMethod;
  /** Părțile, când încasarea a fost plătită din două. `null` altfel. */
  split: PaymentSplitPart[] | null;
  operator: string | null;
  paidOn: string;
  note: string | null;
  advanceUsed: number;
  advanceAdded: number;
  stornoOf: string | null;
  stornoReason: string | null;
  /**
   * Seria chitanței (BANI-5). `null` la încasările de dinainte de numerotare —
   * acelea își păstrează numărul compus din dată și id — și la stornări.
   */
  receiptSeries: string | null;
  /** Numărul chitanței, consecutiv în serie. */
  receiptNo: number | null;
  /** True when a reversal already exists for this payment. */
  reversed: boolean;
  /** Ratele stinse de această încasare, cu suma care a intrat pe fiecare. */
  appliedInst: AppliedTo[];
  /** Procedurile din planul de tratament stinse de această încasare. */
  appliedPhases: AppliedTo[];
  /** Serviciile de pe bon, când casieria le-a detaliat. */
  payItems: Array<{ label: string; amount: number; kind?: string }>;
  /**
   * Cifrele de pe chitanță, așa cum erau la încasare.
   *
   * Fără ele bonul se recompunea din starea de azi: „achitat total" și „rest"
   * creșteau sub o hârtie deja dată pacientului. Null pentru încasările de
   * dinaintea acestei schimbări — atunci bonul cade pe ce știe ecranul.
   */
  receiptSnap: ReceiptSnapshot | null;
  /** Contabilitate §2.6: filiala, entitatea, casa și contul, înghețate la încasare. */
  branchId: string | null;
  legalEntityId: string | null;
  cashDeskId: string | null;
  bankAccountId: string | null;
  /** Bonul fiscal din aparatul de casă, completat la încasare sau după. */
  fiscalReceiptNo: string | null;
  fiscalZNo: string | null;
  fiscalDevice: string | null;
  /** Where the number came from: typed, the desk's agent, or ErpNet.FP. */
  fiscalSource?: 'manual' | 'bridge' | 'erpnet' | null;
  createdAt: string;
}

export interface ReceiptSnapshot {
  /** Ziua încasării. */
  at?: string;
  /** Suma pentru care se dă chitanța: bani plus avansul folosit. */
  now?: number;
  /** Cât achitase pacientul în total, inclusiv plata asta. */
  paidTotal?: number;
  /** Cât îi rămânea de achitat imediat după. */
  outstanding?: number;
  /** Planul, când plata a intrat pe ratele unui singur plan. */
  plan?: { id: string; title: string | null; total: number; paid: number; remaining: number };
}

/** O alocare a unei încasări: pe ce a intrat, și cât. */
export interface AppliedTo {
  id: string;
  amount: number;
}

export interface DailyTotals {
  cash: number;
  card: number;
  transfer: number;
  other: number;
  total: number;
  count: number;
  /** Dat înapoi pacienților în interval (contabilitate §1.6), deja scos din cifrele de sus. */
  refunds: number;
}

export interface Installment {
  id: string;
  seq: number;
  label: string | null;
  dueDate: string | null;
  amount: number;
  paid: number;
  remaining: number;
  paidAt: string | null;
}

export interface PaymentPlan {
  id: string;
  patientId: string;
  title: string | null;
  total: number;
  advance: number;
  type: 'monthly' | 'per_visit' | 'onetime';
  status: 'active' | 'done' | 'cancelled';
  note: string | null;
  createdAt: string;
  paid: number;
  due: number;
  remaining: number;
  installments: Installment[];
}

export interface Outstanding {
  installments: number;
  procedures: number;
  /** Câte proceduri finalizate compun suma de mai sus. */
  proceduresCount: number;
  total: number;
  /** Of the unpaid instalments, how much is already past its date. */
  overdue: number;
  coveredByPlan: boolean;
  advance: number;
}

export const CALL_OUTCOMES = [
  { key: 'promis', label: 'A promis că plătește', tone: 'ok' },
  { key: 'amanat', label: 'A cerut amânare', tone: 'warn' },
  { key: 'nerespuns', label: 'Nu a răspuns', tone: 'neutral' },
  { key: 'refuz', label: 'Refuză să plătească', tone: 'danger' },
] as const;

export type CallOutcome = (typeof CALL_OUTCOMES)[number]['key'];

export interface InstallmentCall {
  id: string;
  installmentId: string;
  outcome: CallOutcome;
  note: string | null;
  followUpOn: string | null;
  calledBy: string | null;
  calledAt: string;
}

export interface OverdueInstallment {
  id: string;
  planId: string;
  planTitle: string | null;
  patientId: string;
  patientName: string;
  phone: string | null;
  label: string | null;
  dueDate: string;
  amount: number;
  paid: number;
  rest: number;
  daysLate: number;
  /** How many times anyone has rung about this instalment. */
  calls: number;
  lastCall: {
    at: string;
    by: string | null;
    outcome: CallOutcome | null;
    note: string | null;
    followUpOn: string | null;
  } | null;
  /** Promised for today or earlier, and still not paid. */
  dueToCallToday: boolean;
}

/** Antetul listei de datorii, numărat pe server peste toată lista, nu pe pagină. */
export interface OverdueSummary {
  count: number;
  rest: number;
  uncalled: number;
  dueToday: number;
}

/** A day counted against the drawer. */
export interface DayClosure {
  id: string;
  closeDate: string;
  /** Casa numărată; `null` — sertarul întregii clinici. */
  cashDeskId: string | null;
  cash: number;
  card: number;
  transfer: number;
  other: number;
  payments: number;
  /** Numerarul dat înapoi pacienților în ziua aceea, din aceeași casă. */
  refunds: number;
  counted: number;
  /** Counted minus cash: negative is money missing. */
  difference: number;
  note: string | null;
  closedBy: string | null;
  closedAt: string;
}

export interface UnpaidProcedure {
  id: string;
  patientId: string;
  patientName: string;
  phone: string | null;
  phase: string;
  teeth: string | null;
  doctor: string | null;
  /** Cine a lucrat: medicul încasării se propune din el. */
  doctorId: string | null;
  /** Programarea la care s-a lucrat — încasarea de pe ea o propune întâi. */
  appointmentId: string | null;
  cost: string;
  paid: string;
  due: string;
  finishedAt: string;
}

/** O lucrare prestată: procedură a fișei sau vizită prețuită, cu cost/achitat/sold. */
export interface PerformedService {
  id: string;
  name: string;
  teeth: string | null;
  doctor: string | null;
  finishedAt: string;
  cost: string;
  paid: string;
  due: string;
}

export interface RecordPaymentInput {
  patientId?: string | null;
  patientName: string;
  appointmentId?: string | null;
  doctorId?: string | null;
  service?: string | null;
  amount: number;
  payMethod: PayMethod;
  paidOn?: string;
  note?: string | null;
  /** Plătitorul, din persoanele legate de pacient. */
  payerPersonId?: string | null;
  advanceUsed?: number;
  appliedPhases?: Array<{ id: string; amount: number }>;
  appliedInst?: Array<{ id: string; amount: number }>;
  /**
   * Plătit din două: părțile, care trebuie să însumeze `amount`.
   *
   * `payMethod` rămâne obligatoriu și se trimite metoda cea mai mare —
   * serverul scrie `mixed` pe rând când primește o împărțire, și tot el
   * verifică suma.
   */
  split?: PaymentSplitPart[];
  /** Contabilitate §2.6 — toate opționale; fără ele, casa și contul implicite ale filialei. */
  cashDeskId?: string | null;
  bankAccountId?: string | null;
  fiscalReceiptNo?: string | null;
  fiscalZNo?: string | null;
  fiscalDevice?: string | null;
  /** Round 2B, accounting add-on only: the certificate spent, the acquirer of a card taking. */
  giftCertificateCode?: string | null;
  acquirerId?: string;
}

/** Mirrors `FiscalReceiptDto`: `null` golește un câmp, un câmp lipsă rămâne cum era. */
/** `GET /payments/:id/ecc-receipt`. */
export interface EccReceiptDraft {
  /** The desk's agent (`dentflow-ecc/1`) or a local ErpNet.FP server. */
  mode: 'bridge' | 'erpnet';
  bridgeUrl: string;
  /** The device's id in ErpNet.FP, for `erpnet`. */
  printerId: string | null;
  deviceModel: string | null;
  printed: { receiptNo: string; zNo: string | null } | null;
  request: EccReceiptRequest;
}

export interface FiscalReceiptInput {
  fiscalReceiptNo?: string | null;
  fiscalZNo?: string | null;
  fiscalDevice?: string | null;
}

export type RefundMethod = 'cash' | 'transfer' | 'card';

/** Banii dați înapoi unui pacient — o dispoziție de plată (contabilitate §1.6). */
export interface PatientRefund {
  id: string;
  legalEntityId: string;
  branchId: string | null;
  patientId: string;
  patientName: string | null;
  paymentId: string | null;
  refundedOn: string;
  /** Negativă pe rândul care stornează o restituire. */
  amount: number;
  method: RefundMethod;
  cashDeskId: string | null;
  bankAccountId: string | null;
  docSeries: string;
  docNo: number;
  reason: string;
  stornoOf: string | null;
  reversed: boolean;
  actor: string | null;
  createdAt: string;
}

/** Mirrors `RecordRefundDto`. */
export interface RecordRefundInput {
  patientId: string;
  amount: number;
  method: RefundMethod;
  reason: string;
  refundedOn?: string;
  cashDeskId?: string | null;
  bankAccountId?: string | null;
  paymentId?: string | null;
}

/** Mirrors `CloseDayDto`. */
export interface CloseDayInput {
  date?: string;
  /** Casa numărată; fără ea, sertarul întregii clinici, ca înainte de case. */
  cashDeskId?: string;
  counted: number;
  note?: string | null;
}

export type ListPaymentsParams = {
  from?: string;
  to?: string;
  patientId?: string;
  doctorId?: string;
  method?: PayMethod;
  limit?: number;
  /** Câte rânduri să sară — pagina următoare. */
  offset?: number;
  /** Numele pacientului sau numărul chitanței, căutat pe server. */
  q?: string;
  /** Serviciul exact, cum e scris pe rând. */
  service?: string;
};

/** Cât cere `listAll` o dată: plafonul serverului pentru `limit`. */
const ALL_PAGE = 1000;

export const paymentsApi = {
  list: (params: ListPaymentsParams) => api.get<Payment[]>('/payments', { query: params }),

  /**
   * Toate încasările care se potrivesc, pagină cu pagină (BANI-7, BANI-8).
   *
   * Pentru ce iese întreg din ecran — exportul, borderoul, istoricul unui
   * pacient — unde o listă tăiată la o limită e o hârtie greșită, nu una
   * scurtă. Se oprește la prima pagină incompletă.
   */
  listAll: async (params: Omit<ListPaymentsParams, 'limit' | 'offset'>): Promise<Payment[]> => {
    const out: Payment[] = [];
    for (let offset = 0; ; offset += ALL_PAGE) {
      const page = await api.get<Payment[]>('/payments', {
        query: { ...params, limit: ALL_PAGE, offset },
      });
      out.push(...page);
      if (page.length < ALL_PAGE) return out;
    }
  },

  totals: (from: string, to: string) =>
    api.get<DailyTotals>('/payments/totals', { query: { from, to } }),

  record: (body: RecordPaymentInput) => api.post<Payment>('/payments', body),

  reverse: (id: string, reason: string) => api.post<Payment>(`/payments/${id}/reverse`, { reason }),

  /** Bonul fiscal, completat după încasare (§5.12). */
  /** The receipt the desk's ECC agent prints for this taking (ECC-INTEGRARE.md). */
  eccReceipt: (id: string) => api.get<EccReceiptDraft>(`/payments/${id}/ecc-receipt`),
  /** What the agent printed; the server checks it and writes the numbers. */
  eccReceiptPrinted: (id: string, body: EccReceiptResult) =>
    api.post<Payment>(`/payments/${id}/ecc-receipt`, body),
  setFiscal: (id: string, body: FiscalReceiptInput) =>
    api.patch<Payment>(`/payments/${id}/fiscal`, body),

  refunds: (query: { from?: string; to?: string; patientId?: string } = {}) =>
    api.get<PatientRefund[]>('/payments/refunds', { query }),
  refund: (body: RecordRefundInput) => api.post<PatientRefund>('/payments/refunds', body),
  reverseRefund: (id: string, reason: string) =>
    api.post<PatientRefund>(`/payments/refunds/${id}/reverse`, { reason }),

  overdue: (params: { q?: string; limit?: number; offset?: number } = {}) =>
    api.get<OverdueInstallment[]>('/payments/debts/overdue', { query: params }),

  overdueSummary: (q?: string) =>
    api.get<OverdueSummary>('/payments/debts/overdue/summary', { query: { q } }),

  calls: (installmentId: string) =>
    api.get<InstallmentCall[]>(`/payments/debts/calls/${installmentId}`),
  recordCall: (
    installmentId: string,
    body: { outcome: CallOutcome; note?: string | null; followUpOn?: string | null },
  ) => api.post<InstallmentCall>(`/payments/debts/calls/${installmentId}`, body),

  /** Fără casă: închiderile întregii clinici; cu ea, doar ale casei. */
  closures: (from?: string, to?: string, cashDeskId?: string) =>
    api.get<DayClosure[]>('/payments/closures', { query: { from, to, cashDeskId } }),
  closeDay: (body: CloseDayInput) => api.post<DayClosure>('/payments/closures', body),
  /** `finishedToday`: doar lucrările terminate azi, în ziua clinicii — pastila „De încasat". */
  unpaidProcedures: (patientId?: string, opts: { finishedToday?: boolean } = {}) =>
    api.get<UnpaidProcedure[]>('/payments/debts/procedures', {
      query: { patientId, finishedToday: opts.finishedToday },
    }),

  outstanding: (patientId: string) =>
    api.get<Outstanding>(`/payments/patients/${patientId}/outstanding`),

  /** Toate lucrările prestate ale pacientului, cu cost/achitat/sold. */
  performedServices: (patientId: string) =>
    api.get<PerformedService[]>(`/payments/patients/${patientId}/performed`),

  advanceLog: (patientId: string) =>
    api.get<
      Array<{
        id: string;
        amount: number;
        balance: number;
        reason: string | null;
        actor: string | null;
        at: string;
      }>
    >(`/payments/patients/${patientId}/advance`),

  adjustAdvance: (patientId: string, delta: number, reason: string) =>
    api.post<{ balance: number }>(`/payments/patients/${patientId}/advance`, { delta, reason }),

  updatePlan: (
    id: string,
    body: { title?: string | null; note?: string | null; status?: 'active' | 'done' | 'cancelled' },
  ) => api.patch<PaymentPlan>(`/payments/plans/${id}`, body),

  addInstallment: (
    planId: string,
    body: { amount: number; label?: string | null; dueDate?: string | null },
  ) => api.post<PaymentPlan>(`/payments/plans/${planId}/installments`, body),

  /**
   * Bani primiți înainte de scadență, peste ratele unui plan.
   *
   * `stinge` plătește ratele următoare în ordine — rămân mai puține plăți.
   * `reduce` le micșorează pe toate cele rămase — rămân tot atâtea, mai mici.
   */
  /** Anulează o stornare: încasarea revine așa cum era. */
  restore: (id: string) => api.post<Payment>(`/payments/${id}/restore`, {}),

  planAdvance: (
    planId: string,
    body: {
      amount: number;
      strategy: 'stinge' | 'reduce';
      payMethod?: PayMethod;
      advanceUsed?: number;
      note?: string | null;
    },
  ) => api.post<Payment>(`/payments/plans/${planId}/advance`, body),

  updateInstallment: (
    id: string,
    body: { amount?: number; label?: string | null; dueDate?: string | null },
  ) => api.patch<PaymentPlan>(`/payments/installments/${id}`, body),

  deleteInstallment: (id: string) => api.delete<PaymentPlan>(`/payments/installments/${id}`),

  plans: (patientId?: string) =>
    api.get<PaymentPlan[]>('/payments/plans', { query: { patientId } }),

  previewPlan: (body: {
    total: number;
    type: string;
    advancePct?: number;
    installments?: number;
    dueDay?: number;
    rounding?: number;
    startDate?: string;
    discountPct?: number;
  }) => api.post<Installment[]>('/payments/plans/preview', body),

  createPlan: (body: {
    patientId: string;
    title?: string | null;
    total: number;
    advance?: number;
    type: string;
    note?: string | null;
    installments: Array<{
      seq: number;
      label?: string | null;
      dueDate?: string | null;
      amount: number;
    }>;
  }) => api.post<{ id: string }>('/payments/plans', body),

  cancelPlan: (id: string, reason?: string) =>
    api.delete<void>(`/payments/plans/${id}`, { query: { reason } }),
};

export const PAY_METHOD_LABELS = i18nMap<PayMethod>('common', {
  cash: 'numerar',
  card: 'card',
  transfer: 'transfer',
  other: 'altaMetoda',
  mixed: 'mixt',
  online: 'plataOnline',
  certificate: 'certificatCadou',
});

/** The reference's own marks, so a cash desk picks by shape as much as word. */
export const PAY_METHOD_ICONS: Record<PayMethod, string> = {
  cash: '💵',
  card: '💳',
  transfer: '🏦',
  other: '🧾',
  mixed: '🧮',
  online: '🌐',
  certificate: '🎁',
};
